<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    placement="top"
    wide
    :title="title"
    :subtitle="subtitle"
    :icon="ICONS.actions.search"
    @close="emit('close')"
  >
    <div class="stack tight">
      <label class="sr-only" :for="inputId">Search characters</label>
      <input
        :id="inputId"
        ref="inputRef"
        v-model="query"
        class="ui-input stat-mono"
        type="text"
        autocomplete="off"
        spellcheck="false"
        placeholder="Search characters…"
        @keydown="handleKeydown"
      />

      <div class="list" role="listbox" aria-label="Characters">
        <button
          v-for="(c, idx) in filtered"
          :key="c.id"
          type="button"
          class="row"
          :class="{ active: c.id === currentId.value, selected: idx === activeIndex }"
          role="option"
          :aria-selected="idx === activeIndex"
          @mouseenter="activeIndex = idx"
          @click="select(c.id)"
        >
          <span class="icon" aria-hidden="true">{{ ICONS.sessions.area }}</span>
          <span class="main">
            <span class="name">{{ c.name }}</span>
            <span class="meta text-muted stat-mono num">
              Lv {{ formatNumber(c.skillProgress?.find((s) => s.id === 'character')?.level ?? 1) }} · {{ c.areaId }}
            </span>
          </span>
          <UiChip v-if="c.id === currentId.value" variant="accent">Current</UiChip>
        </button>

        <UiCallout v-if="filtered.length === 0" variant="info">No matches.</UiCallout>
      </div>

      <div class="switcher-actions">
        <UiButton size="sm" variant="secondary" @click="goToRoster">
          <template #icon>{{ ICONS.actions.switchCharacter }}</template>
          Roster / Create
        </UiButton>
      </div>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import type { Character } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiButton from "../../../components/ui/UiButton.vue";

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    title?: string;
    subtitle?: string;
    characters: Ref<Character[]>;
    currentId: Ref<string | null>;
    onSelect: (id: string) => void;
  }>(),
  {
    overlayZIndex: 4000,
    title: "Switch Character",
    subtitle: "Swap characters from anywhere. Server state continues."
  }
);

const emit = defineEmits<{ close: [] }>();
const router = useRouter();

const inputRef = ref<HTMLInputElement | null>(null);
const query = ref("");
const activeIndex = ref(0);
const inputId = `char_${Math.random().toString(16).slice(2)}`;

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  const list = props.characters.value ?? [];
  if (!q) return list;
  return list.filter((c) => `${c.name} ${c.areaId}`.toLowerCase().includes(q));
});

function clampActive() {
  if (filtered.value.length === 0) {
    activeIndex.value = 0;
    return;
  }
  activeIndex.value = Math.max(0, Math.min(filtered.value.length - 1, activeIndex.value));
}

function select(id: string) {
  props.onSelect(id);
  emit("close");
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    activeIndex.value += 1;
    clampActive();
    return;
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    activeIndex.value -= 1;
    clampActive();
    return;
  }
  if (event.key === "Home") {
    event.preventDefault();
    activeIndex.value = 0;
    return;
  }
  if (event.key === "End") {
    event.preventDefault();
    activeIndex.value = Math.max(0, filtered.value.length - 1);
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const next = filtered.value[activeIndex.value];
    if (next) select(next.id);
    return;
  }
}

function goToRoster() {
  emit("close");
  router.push("/characters");
}

onMounted(async () => {
  await nextTick();
  inputRef.value?.focus();
});
</script>

<style scoped>
.list {
  display: grid;
  gap: 0.55rem;
  max-height: 360px;
  overflow: auto;
}

.switcher-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.row {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--border-0);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.row.selected {
  border-color: color-mix(in srgb, var(--accent) 55%, transparent);
  box-shadow: var(--shadow-accent);
}

.row.active {
  background: color-mix(in srgb, var(--accent) 10%, var(--bg-2));
}

.icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--bg-1) 75%, transparent);
  border: 1px solid var(--border-0);
}

.main {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.name {
  font-weight: 850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
